import { z } from 'zod';
import {
  BoardPackView,
  ChangeEventRow,
  PeriodComparisonView,
  QueryMetadata,
  RiskRegisterView,
  SavedViewRow,
  ShareLinkRow,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * Reporting, sharing and the risk register, over HTTP.
 *
 * Everything the C7 work added reaches the API through here. The reads sit
 * behind `Repositories` where the port already has a method for them; the
 * commands do not, for the reason every other write in this app does not —
 * the port is a list of questions a screen asks, and raising a risk is not a
 * question.
 *
 * `parse` on every read is not ceremony. Three of these payloads carry the
 * distinction between "restricted", "not tracked" and a real figure, and a
 * schema that silently accepted a number where the contract says nullable is
 * the failure mode this product exists to prevent.
 */

export type WriteOutcome<T = void> =
  | { ok: true; value: T }
  | { ok: false; message: string; problems?: string[] };

async function refusal<T>(response: Response): Promise<WriteOutcome<T>> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    problems?: string[];
  };
  return {
    ok: false,
    message: body.message ?? `Request failed (${response.status}).`,
    problems: body.problems,
  };
}

const RiskEnvelope = z.object({ data: RiskRegisterView, meta: QueryMetadata });
const CompareEnvelope = z.object({ data: PeriodComparisonView, meta: QueryMetadata });
const PackEnvelope = z.object({ data: BoardPackView, meta: QueryMetadata });
const SharesEnvelope = z.object({ data: z.array(ShareLinkRow), meta: QueryMetadata });
const ViewsEnvelope = z.object({ data: z.array(SavedViewRow), meta: QueryMetadata });
const EventsEnvelope = z.object({
  data: z.object({ rows: z.array(ChangeEventRow), from: z.string(), to: z.string() }),
  meta: QueryMetadata,
});

export interface RiskDraft {
  title: string;
  description?: string | null;
  subjectEntityId?: string | null;
  category: string;
  likelihood: string;
  impact: string;
  mitigation?: string | null;
}

export interface ShareDraft {
  label: string;
  viewRoute: string;
  viewQuery?: string;
  expiresInDays: number;
}

export interface MintedShare {
  id: string;
  token: string;
  url: string;
  expiresInDays: number;
  scopeLabel: string;
  canSeeFinance: boolean;
}

export function createHttpReportingRepository(baseUrl: string) {
  return {
    // ── Risk register ──────────────────────────────────────────────────────
    async getRiskRegister(
      filters: GlobalFilters,
      refine?: { status?: string; subjectId?: string },
    ): Promise<QueryResult<RiskRegisterView>> {
      const params = filterParams(filters);
      if (refine?.status) params.set('status', refine.status);
      if (refine?.subjectId) params.set('subjectId', refine.subjectId);
      const response = await apiFetch(`${baseUrl}/risks?${params.toString()}`);
      if (!response.ok) throw new Error(`GET /risks failed: ${response.status}`);
      return RiskEnvelope.parse((await response.json()) as unknown);
    },

    async raiseRisk(draft: RiskDraft): Promise<WriteOutcome<string>> {
      const response = await apiFetch(`${baseUrl}/risks`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { data: { id: string } };
      return { ok: true, value: body.data.id };
    },

    async updateRisk(
      riskId: string,
      change: {
        status?: string;
        likelihood?: string;
        impact?: string;
        mitigation?: string | null;
        reason: string;
      },
    ): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/risks/${riskId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(change),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    // ── Period comparison ──────────────────────────────────────────────────
    async comparePeriods(
      filters: GlobalFilters,
      windows?: { baselineStart?: string; baselineEnd?: string; comparisonStart?: string; comparisonEnd?: string },
    ): Promise<QueryResult<PeriodComparisonView>> {
      const params = filterParams(filters);
      for (const [key, value] of Object.entries(windows ?? {})) {
        if (value) params.set(key, value);
      }
      const response = await apiFetch(`${baseUrl}/portfolio/compare?${params.toString()}`);
      if (!response.ok) throw new Error(`GET /portfolio/compare failed: ${response.status}`);
      return CompareEnvelope.parse((await response.json()) as unknown);
    },

    // ── Change events ──────────────────────────────────────────────────────
    async getChangeEvents(window?: { from?: string; to?: string }) {
      const params = new URLSearchParams();
      if (window?.from) params.set('from', window.from);
      if (window?.to) params.set('to', window.to);
      const response = await apiFetch(`${baseUrl}/change-events?${params.toString()}`);
      if (!response.ok) throw new Error(`GET /change-events failed: ${response.status}`);
      return EventsEnvelope.parse((await response.json()) as unknown);
    },

    async recordChangeEvent(draft: {
      occurredOn: string;
      category: string;
      title: string;
      description?: string | null;
    }): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/change-events`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    // ── Board pack ─────────────────────────────────────────────────────────
    async getBoardPack(filters: GlobalFilters): Promise<QueryResult<BoardPackView>> {
      const response = await apiFetch(
        `${baseUrl}/reports/board-pack?${filterParams(filters).toString()}`,
      );
      if (!response.ok) throw new Error(`GET /reports/board-pack failed: ${response.status}`);
      return PackEnvelope.parse((await response.json()) as unknown);
    },

    /**
     * The document itself, as a file the browser saves.
     *
     * Fetched and turned into an object URL rather than pointed at with an
     * anchor, because the request needs the identity header — an `<a href>`
     * would arrive without one and be refused, which is how a download button
     * ends up producing a JSON error page named `.html`.
     */
    boardPackUrl(filters: GlobalFilters): string {
      return `${baseUrl}/reports/board-pack.html?${filterParams(filters).toString()}`;
    },

    async downloadBoardPack(filters: GlobalFilters): Promise<WriteOutcome<{ blob: Blob; filename: string }>> {
      const response = await apiFetch(this.boardPackUrl(filters));
      if (!response.ok) return refusal(response);
      const disposition = response.headers.get('content-disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      return {
        ok: true,
        value: {
          blob: await response.blob(),
          filename: match?.[1] ?? 'PVI_Portfolio_Review.html',
        },
      };
    },

    // ── Shares ─────────────────────────────────────────────────────────────
    async listShares(): Promise<QueryResult<ShareLinkRow[]>> {
      const response = await apiFetch(`${baseUrl}/shares`);
      if (!response.ok) throw new Error(`GET /shares failed: ${response.status}`);
      return SharesEnvelope.parse((await response.json()) as unknown);
    },

    async createShare(draft: ShareDraft): Promise<WriteOutcome<MintedShare>> {
      const response = await apiFetch(`${baseUrl}/shares`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { data: MintedShare };
      return { ok: true, value: body.data };
    },

    async revokeShare(shareId: string): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/shares/${shareId}/revoke`, { method: 'POST' });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    /**
     * Resolves a share for a reader who has no account.
     *
     * Deliberately not through `apiFetch`: that attaches the signed-in
     * identity, and the whole point of this call is that there is not one. A
     * share must render the same for a stranger as for the person who made it,
     * and quietly sending a header would hide it if it did not.
     */
    async readShare(token: string): Promise<
      | { ok: true; pack: BoardPackView; share: { label: string; sharedBy: string | null } }
      | { ok: false; message: string }
    > {
      const response = await fetch(`${baseUrl}/share/${encodeURIComponent(token)}`);
      const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (!response.ok) {
        return {
          ok: false,
          message:
            (body.message as string) ??
            'This shared view could not be opened. Ask the person who sent it for a current link.',
        };
      }
      return {
        ok: true,
        pack: BoardPackView.parse(body.data),
        share: body.share as { label: string; sharedBy: string | null },
      };
    },

    // ── Saved views ────────────────────────────────────────────────────────
    async listSavedViews(): Promise<QueryResult<SavedViewRow[]>> {
      const response = await apiFetch(`${baseUrl}/me/views`);
      if (!response.ok) throw new Error(`GET /me/views failed: ${response.status}`);
      return ViewsEnvelope.parse((await response.json()) as unknown);
    },

    async saveView(draft: {
      name: string;
      viewRoute: string;
      viewQuery: string;
      grouping?: string | null;
    }): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/me/views`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    async deleteSavedView(viewId: string): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/me/views/${viewId}`, { method: 'DELETE' });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    async markViewOpened(viewId: string): Promise<void> {
      await apiFetch(`${baseUrl}/me/views/${viewId}/open`, { method: 'POST' }).catch(
        () => undefined,
      );
    },
  };
}
